import {DateFilterProvider} from '@github-ui/filter-core/providers'
import {ArrowSwitchIcon, CalendarIcon} from '@primer/octicons-react'
import {singleValueFilterTypes} from '@github-ui/repos-rules-types/shared-filter-types'
import {
  DEFAULT_FILTER_PRIORITY,
  FilterQuery,
  getUnescapedFilterValue,
  ValidationTypes,
  type FilterBlock,
  type FilterConfig,
  type FilterSuggestion,
  type IndexedBlockValueItem,
} from '@github-ui/filter-core'

const DYNAMIC_DATE_REGEX = /^(>|<|>=|<=)?(@[A-Za-z]+)([+-])(\d+)([mdwqy])?$/

function getCurrentDate() {
  return new Date().toLocaleDateString('en-us', {year: 'numeric', month: 'long', day: 'numeric'})
}

export const TIME_RANGE_VALUES: FilterSuggestion[] = [
  {
    value: '@today',
    displayName: 'Today',
    priority: 1,
    icon: ArrowSwitchIcon,
    iconColor: 'var(--fgColor-done, var(--color-done-fg))',
  },
  {
    value: '>@today-1w',
    displayName: 'Past 7 days',
    priority: 2,
    icon: ArrowSwitchIcon,
    iconColor: 'var(--fgColor-done, var(--color-done-fg))',
  },
  {
    value: '>@today-30d',
    displayName: 'Past 30 days',
    priority: 3,
    icon: ArrowSwitchIcon,
    iconColor: 'var(--fgColor-done, var(--color-done-fg))',
  },
  {
    value: () => {
      const now = new Date()
      return `${now.getFullYear()}-${`0${now.getMonth() + 1}`.slice(-2)}-${`0${now.getDate()}`.slice(-2)}`
    },
    displayName: getCurrentDate(),
    priority: 4,
    icon: CalendarIcon,
  },
]

export class CreatedFilterProvider extends DateFilterProvider {
  #limitRangeByMonths?: number

  constructor({limitRangeByMonths}: {limitRangeByMonths?: number}) {
    super(
      {
        displayName: 'Creation date',
        key: 'created',
        priority: DEFAULT_FILTER_PRIORITY,
        icon: CalendarIcon,
        description: 'Creation date',
      },
      TIME_RANGE_VALUES,
      {
        filterTypes: singleValueFilterTypes,
        priority: DEFAULT_FILTER_PRIORITY,
      },
    )
    this.#limitRangeByMonths = limitRangeByMonths ?? undefined
  }

  override validateFilterBlockValues(
    filterQuery: FilterQuery,
    block: FilterBlock,
    values: IndexedBlockValueItem[],
    _: FilterConfig,
  ): IndexedBlockValueItem[] {
    const blockValueItems = super.validateFilterBlockValues(filterQuery, block, values, _)
    if (typeof this.#limitRangeByMonths === 'undefined') {
      return blockValueItems
    }
    const limit = this.#limitRangeByMonths

    return blockValueItems.map(valueItem => {
      if (!valueItem.valid) {
        return valueItem
      }

      const updatedInvalidation = {
        ...valueItem,
        valid: false,
        validations: [
          {
            type: ValidationTypes.InvalidValue,
            message: `Query must be within the last ${limit === 1 ? 'month' : `${limit} months`}`,
          },
        ],
      }

      return this.#isExtractedValueValid(valueItem) ? valueItem : updatedInvalidation
    })
  }

  #isExtractedValueValid(valueItem: IndexedBlockValueItem): boolean {
    const extractedValue = getUnescapedFilterValue(valueItem.value) as string // valid blocks will have a value
    const dynamicMatch = DYNAMIC_DATE_REGEX.exec(extractedValue)

    const limit = this.#limitRangeByMonths as number
    if (dynamicMatch) {
      const [, directionOperator, startDate, differenceOperator, timeSpan, timeSpanType] = dynamicMatch
      if (directionOperator === '<' || directionOperator === '<=') {
        return false
      }

      if (startDate === '@today' && differenceOperator === '-') {
        const parsedTimeSpan = parseInt(timeSpan as string)
        if (
          (timeSpanType === 'y' && parsedTimeSpan > limit / 12) ||
          (timeSpanType === 'q' && parsedTimeSpan > limit / 3) ||
          (timeSpanType === 'm' && parsedTimeSpan > limit) ||
          (timeSpanType === 'w' && parsedTimeSpan > limit * 4) ||
          (timeSpanType === 'd' && parsedTimeSpan > limit * 30)
        ) {
          return false
        }
      }
    } else {
      const date = this.extractDateFromBlockValue(extractedValue)

      if (date) {
        const now = new Date()
        const monthsAgo = now.getTime() - limit * 30 * 24 * 60 * 60 * 1000

        if (Date.parse(date) < monthsAgo) {
          return false
        }
      }
    }
    return true
  }
}
